// Mapbox helpers for the satellite timelapse image-source layer.
//
// Kept as pure functions that take a map instance so the CourseMapPage can
// manage the layer lifecycle from its existing `renderX(map, ...)` pattern
// without shimming a React context around Mapbox's imperative API.

import type { Map as MapboxMap, ImageSource } from 'mapbox-gl';
import type { TimelapseFrame } from '../api/satellite.js';

export const TIMELAPSE_SOURCE_ID = 'satellite-timelapse';
export const TIMELAPSE_LAYER_ID = 'satellite-timelapse-fill';

/** Mapbox `image` source expects 4 corners clockwise starting top-left. */
function bboxCorners(
  bbox: [number, number, number, number],
): [[number, number], [number, number], [number, number], [number, number]] {
  const [west, south, east, north] = bbox;
  return [
    [west, north],
    [east, north],
    [east, south],
    [west, south],
  ];
}

/** Add or update the timelapse image source to point at `frame`. Layer is
 *  inserted before `beforeLayerId` so zone polygons + sensor dots paint over
 *  the imagery. Opacity is tunable so the scrubber bar can fade to emphasize
 *  zones if needed. */
export function renderTimelapseFrame(
  map: MapboxMap,
  frame: TimelapseFrame,
  opacity: number,
  beforeLayerId?: string,
): void {
  const coords = bboxCorners(frame.bbox);
  const existing = map.getSource(TIMELAPSE_SOURCE_ID) as ImageSource | undefined;
  if (existing) {
    existing.updateImage({ url: frame.url, coordinates: coords });
    if (map.getLayer(TIMELAPSE_LAYER_ID)) {
      map.setPaintProperty(TIMELAPSE_LAYER_ID, 'raster-opacity', opacity);
    }
    return;
  }
  map.addSource(TIMELAPSE_SOURCE_ID, {
    type: 'image',
    url: frame.url,
    coordinates: coords,
  });
  map.addLayer(
    {
      id: TIMELAPSE_LAYER_ID,
      source: TIMELAPSE_SOURCE_ID,
      type: 'raster',
      paint: {
        'raster-opacity': opacity,
        // 150ms cross-fade between frame swaps — smooth enough for a
        // timelapse to feel alive, short enough to stay responsive under
        // a user drag.
        'raster-fade-duration': 150,
      },
    },
    beforeLayerId,
  );
}

/** Tear down the timelapse layer + source (no-op if absent). */
export function removeTimelapse(map: MapboxMap): void {
  if (map.getLayer(TIMELAPSE_LAYER_ID)) map.removeLayer(TIMELAPSE_LAYER_ID);
  if (map.getSource(TIMELAPSE_SOURCE_ID)) map.removeSource(TIMELAPSE_SOURCE_ID);
}

const prefetched = new Set<string>();

/** Pre-warm the browser's HTTP cache for frames near `centerIndex` so swaps
 *  during auto-play don't stall on network. Fire-and-forget; idempotent. */
export function prefetchNearbyFrames(
  frames: TimelapseFrame[],
  centerIndex: number,
  radius = 3,
): void {
  const lo = Math.max(0, centerIndex - radius);
  const hi = Math.min(frames.length - 1, centerIndex + radius);
  for (let i = lo; i <= hi; i++) {
    const f = frames[i];
    if (!f || prefetched.has(f.url)) continue;
    prefetched.add(f.url);
    const img = new Image();
    img.src = f.url;
  }
}

/** Human-friendly month label for the scrubber chip. */
export function frameLabel(frameDate: string): string {
  const [y, m] = frameDate.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const mi = Number(m) - 1;
  const day = Number(frameDate.slice(8, 10));
  const dayLabel = day <= 7 ? 'early' : day <= 21 ? 'mid' : 'late';
  return `${dayLabel} ${monthNames[mi] ?? m} ${y}`;
}
